/**
 * Radix UI Dialog / Popover / DropdownMenu pointer-events leak fix.
 *
 * Why this exists
 * ---------------
 * Radix's modal primitives temporarily set `pointer-events: none` on
 * `document.body` while a modal is open so background clicks can't
 * activate underlying controls. On close, Radix is supposed to remove
 * that inline style — but in several known scenarios it never does:
 *
 *   - A toast appears during/just after the close animation
 *   - The user navigates (router push) while the dialog is closing
 *   - Two overlapping modals close in the wrong order
 *   - A `setState` triggers a re-render mid-cleanup
 *
 * The result the merchant sees: the page looks normal, but NOTHING is
 * clickable. They think the site "froze" and have to refresh. This is
 * the #1 cause of users reporting "تتجمّد عند فتح/حفظ/رفع" in
 * shadcn/Radix codebases.
 *
 * Upstream issue: https://github.com/radix-ui/primitives/issues/1241
 *
 * The fix
 * -------
 * A MutationObserver watches `<body>` for `style` changes. Any time
 * `pointer-events: none` is set, we wait one animation frame for
 * Radix's own cleanup to finish, then check whether ANY actual modal
 * (`[role="dialog"]`, `[data-state="open"]` on Radix overlays, etc.)
 * is still in the DOM. If nothing modal-like is open, the lock is
 * stale and we strip it.
 *
 * The check is cheap (single querySelector) and only runs when
 * pointer-events actually changes, so the runtime cost is essentially
 * zero on a healthy page.
 */

let installed = false;

function isAnyRadixOverlayOpen(): boolean {
  // Radix marks open primitives with data-state="open" on their root.
  // We also check for a visible dialog/menu role as a belt-and-braces
  // signal in case data-state was already removed but the element is
  // mid-unmount.
  return Boolean(
    document.querySelector(
      [
        '[data-state="open"][role="dialog"]',
        '[data-state="open"][role="alertdialog"]',
        '[data-state="open"][role="menu"]',
        '[data-state="open"][role="listbox"]',
        '[data-radix-popper-content-wrapper]',
        '[data-radix-portal] [data-state="open"]',
      ].join(','),
    ),
  );
}

export function installDialogPointerEventsFix(): void {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;

  const body = document.body;
  if (!body) return;

  const cleanupIfStale = () => {
    // Radix sets the inline style synchronously; its cleanup happens
    // on the next tick. Wait two RAFs to be safe before deciding the
    // lock is stale.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (body.style.pointerEvents === 'none' && !isAnyRadixOverlayOpen()) {
          body.style.pointerEvents = '';
          // Some Radix versions also set the attribute via a CSS
          // variable / `aria-hidden` on siblings — clear those too.
          if (body.getAttribute('style')?.trim() === '') {
            body.removeAttribute('style');
          }
        }
      });
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'style') {
        if (body.style.pointerEvents === 'none') {
          cleanupIfStale();
        }
      }
    }
  });

  observer.observe(body, { attributes: true, attributeFilter: ['style'] });

  // Also run a periodic safety net every 3s — covers the edge case
  // where the MutationObserver missed the lock (e.g. body was
  // replaced). This is intentionally rare so it never shows up in
  // a profile.
  window.setInterval(() => {
    if (body.style.pointerEvents === 'none' && !isAnyRadixOverlayOpen()) {
      body.style.pointerEvents = '';
    }
  }, 3000);

  // Final safety net: when the user clicks/taps anywhere and nothing
  // is open, strip the lock immediately so they're not stuck waiting
  // for the next observer tick.
  const releaseOnInteraction = () => {
    if (body.style.pointerEvents === 'none' && !isAnyRadixOverlayOpen()) {
      body.style.pointerEvents = '';
    }
  };
  window.addEventListener('pointerdown', releaseOnInteraction, { capture: true });
  window.addEventListener('keydown', releaseOnInteraction, { capture: true });
}
